import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Church, Home, Video, Bell, Calendar, Users, Heart, Image, Mail, Settings,
  MessageSquare, Menu, X, LogOut, ChevronRight, User
} from 'lucide-react';

// Admin Pages
import AdminHome from './AdminHome';
import AdminSermons from './AdminSermons';
import AdminAnnouncements from './AdminAnnouncements';
import AdminEvents from './AdminEvents';
import AdminStaff from './AdminStaff';
import AdminPrayerRequests from './AdminPrayerRequests';
import AdminGalleries from './AdminGalleries';
import AdminMembers from './AdminMembers';
import AdminNewsletter from './AdminNewsletter';
import AdminMessages from './AdminMessages';
import AdminSettings from './AdminSettings';
import AdminUsers from './AdminUsers';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: Home },
    { name: 'Sermons', href: '/admin/sermons', icon: Video },
    { name: 'Announcements', href: '/admin/announcements', icon: Bell },
    { name: 'Events', href: '/admin/events', icon: Calendar },
    { name: 'Staff', href: '/admin/staff', icon: Users },
    { name: 'Prayer Requests', href: '/admin/prayer-requests', icon: Heart },
    { name: 'Galleries', href: '/admin/galleries', icon: Image },
    { name: 'Members', href: '/admin/members', icon: User },
    { name: 'Newsletter', href: '/admin/newsletter', icon: Mail },
    { name: 'Messages', href: '/admin/messages', icon: MessageSquare },
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'Settings', href: '/admin/settings', icon: Settings },
  ];

  const isActive = (href) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-stone-800 transform transition-transform duration-200 lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-4 py-5 border-b border-stone-700">
            <Church className="h-8 w-8 text-amber-500" />
            <div>
              <h1 className="font-semibold text-white">St. John's UCC</h1>
              <p className="text-xs text-stone-400">Admin Dashboard</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                  isActive(item.href)
                    ? "bg-amber-700 text-white"
                    : "text-stone-300 hover:bg-stone-700 hover:text-white"
                )}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </Link>
            ))}
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-stone-700">
            <div className="flex items-center gap-3 mb-3">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-amber-700 flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-stone-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-stone-300 hover:text-white hover:bg-stone-700"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-stone-100"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </button>
              <Link to="/" className="text-sm text-amber-700 hover:underline flex items-center">
                <ChevronRight className="h-4 w-4 rotate-180" />
                View Site
              </Link>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6">
          <Routes>
            <Route index element={<AdminHome />} />
            <Route path="sermons/*" element={<AdminSermons />} />
            <Route path="announcements/*" element={<AdminAnnouncements />} />
            <Route path="events/*" element={<AdminEvents />} />
            <Route path="staff/*" element={<AdminStaff />} />
            <Route path="prayer-requests/*" element={<AdminPrayerRequests />} />
            <Route path="galleries/*" element={<AdminGalleries />} />
            <Route path="members/*" element={<AdminMembers />} />
            <Route path="newsletter/*" element={<AdminNewsletter />} />
            <Route path="messages/*" element={<AdminMessages />} />
            <Route path="users/*" element={<AdminUsers />} />
            <Route path="settings/*" element={<AdminSettings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
